import type {
  GenerationImpactResult,
  GenerationLayoutManualPlacement,
  GenerationProjectInstalledReservations,
  GenerationProjectReservationEnvelope,
} from '../../types/generationImpact';

/** Exact conversion used by the site-boundary and generation-impact models. */
export const SQUARE_METRES_PER_ACRE = 4_046.8564224;

export type GenerationTestFitOrientation =
  | 'auto'
  | 'blocks-along-width'
  | 'blocks-along-height';

export type GenerationTestFitResolvedOrientation = Exclude<
  GenerationTestFitOrientation,
  'auto'
>;

export type GenerationTestFitComponentKind =
  | 'generation-block'
  | 'cooling'
  | 'switchyard'
  | 'ccs-capture'
  | 'ccs-compression'
  | 'natural-gas-route'
  | 'co2-export-route'
  | 'construction-laydown'
  // Test-Fit Studio kinds (layout schemaVersion 2). 'access-road' is
  // packer-emitted (row corridors become visible objects); the rest are
  // user-defined custom components, always manually placed, never packed.
  | 'access-road'
  | 'road'
  | 'parking'
  | 'building'
  | 'data-center'
  | 'generator'
  | 'custom';

export type GenerationTestFitDimensionBasis =
  | 'planning-acreage'
  | 'project-installed-dimensions'
  | 'project-route-reservation'
  | 'project-construction-reservation'
  /** Studio custom components: dimensions entered or drawn by the user. */
  | 'user-defined';

export type GenerationTestFitLifecycle =
  | 'permanent'
  | 'route-reservation'
  | 'construction';

export type GenerationTestFitPackingScheme = 'compact' | 'road-rows';

/**
 * Solver footprint fields consumed by the test-fit model. A full
 * `GenerationImpactResult['footprint']` is structurally compatible.
 */
export type GenerationTestFitFootprint = Pick<
  GenerationImpactResult['footprint'],
  | 'plantAcres'
  | 'coolingAcres'
  | 'switchyardAcres'
  | 'ccsAcres'
  | 'compressionAcres'
>;

/** Local, axis-aligned parcel envelope available to the screening model. */
export interface GenerationTestFitEnvelope {
  /** Local x-axis span, metres. */
  widthM: number;
  /** Local y-axis span, metres. */
  heightM: number;
}

/**
 * Rectangle width-to-height ratios in the selected layout orientation.
 * Omitted values use a neutral square (1:1) planning block.
 */
export interface GenerationTestFitAspectRatios {
  generationBlock?: number;
  cooling?: number;
  switchyard?: number;
  ccsCapture?: number;
  ccsCompression?: number;
}

export interface GenerationTestFitInput {
  usableEnvelope: GenerationTestFitEnvelope;
  /** Number of equal-area generation blocks represented by plantAcres. */
  generationBlockCount: number;
  footprint: GenerationTestFitFootprint;
  /** Clear perimeter reserved inside the usable envelope, metres. */
  edgeSetbackM: number;
  /** Clear distance between adjacent planning rectangles, metres. */
  componentGapM: number;
  orientation?: GenerationTestFitOrientation;
  aspectRatios?: GenerationTestFitAspectRatios;
  /** Exact project envelopes; omitted components continue to use planning acreage. */
  installedReservations?: GenerationProjectInstalledReservations;
  /** Access-road corridor between generation-block rows. The corridor is the
   * larger of this value and componentGapM. */
  rowRoadWidthM?: number;
  /** 'auto' compares road-rows against compact and prefers road-rows when it
   * also fits. Omitted -> compact (legacy behaviour, byte-identical). */
  packingScheme?: GenerationTestFitPackingScheme | 'auto';
  /**
   * Test-Fit Studio semantics gate. ABSENT = legacy behaviour preserved
   * byte-identical (including the road-gap `max()` no-op and the v3 stamp on
   * geometry identical to compact). 2 enables: pin+pack manual placements,
   * additive road corridors (componentGapM + rowRoadWidthM), emitted
   * `access-road` rectangles, honest `auto`, and honest model stamping.
   */
  schemaVersion?: 2;
  /** Studio pins — requires schemaVersion 2. Pinned items are excluded from
   * shelf packing and placed exactly at their layout-frame coordinates;
   * duplicates for one componentId resolve last-wins. */
  manualPlacements?: readonly GenerationLayoutManualPlacement[];
}

export interface GenerationTestFitLocalPoint {
  /** Offset from the usable envelope's centre, metres. */
  xM: number;
  /** Offset from the usable envelope's centre, metres. */
  yM: number;
}

export interface GenerationTestFitRectangle {
  id: string;
  label: string;
  kind: GenerationTestFitComponentKind;
  /** One-based only for generation blocks. */
  blockNumber?: number;
  areaAcres: number;
  /** Rectangle-centre offset from the usable envelope centre, metres. */
  xM: number;
  /** Rectangle-centre offset from the usable envelope centre, metres. */
  yM: number;
  widthM: number;
  heightM: number;
  /** Closed polygon in the same local-metre convention as xM/yM. */
  polygonM: readonly GenerationTestFitLocalPoint[];
  fitsWithinEnvelope: boolean;
  dimensionBasis: GenerationTestFitDimensionBasis;
  lifecycle: GenerationTestFitLifecycle;
  /** Studio pin+pack (schemaVersion 2): CCW rotation about the rectangle
   *  centre relative to the layout axes. OMITTED for packed rectangles so
   *  legacy output JSON stays byte-identical. */
  rotationDeg?: number;
  /** 'pinned' when a manual placement fixed this rectangle; omitted for
   *  packer-placed rectangles (legacy byte-stability). */
  placement?: 'packed' | 'pinned';
}

export interface GenerationTestFitOrientationEvaluation {
  orientation: GenerationTestFitResolvedOrientation;
  fits: boolean;
  requiredWidthM: number;
  requiredHeightM: number;
  widthOverflowM: number;
  heightOverflowM: number;
  /** Bounding rectangle area, including component gaps and edge setbacks. */
  boundingAreaAcres: number;
}

export interface GenerationTestFitSchemeEvaluation extends GenerationTestFitOrientationEvaluation {
  scheme: GenerationTestFitPackingScheme;
}

export interface GenerationTestFitResult {
  /** Legacy: v3 whenever a road-rows packing was selected (even when the
   * corridor was a no-op — bug preserved for frozen replay), otherwise v2.
   * schemaVersion 2: v4 whenever studio features shaped the geometry (pins
   * applied or access-road corridors emitted), otherwise v2 — geometry
   * without studio features is byte-identical to the legacy compact path. */
  modelVersion: 'generation-test-fit-v2' | 'generation-test-fit-v3' | 'generation-test-fit-v4';
  status: 'fit' | 'overflow';
  orientation: GenerationTestFitResolvedOrientation;
  usableEnvelope: GenerationTestFitEnvelope & { areaAcres: number };
  /** Envelope remaining after applying edgeSetbackM on all four sides. */
  packingEnvelope: GenerationTestFitEnvelope & { areaAcres: number };
  /** Outer envelope required by the packed rectangles plus edge setbacks.
   * With pins applied it covers the packed group AND every pinned rectangle. */
  requiredEnvelope: GenerationTestFitEnvelope & { areaAcres: number };
  /** Present only when pins are applied: the envelope required by the FREE
   * (packer-translated) group alone. The exclusion-aware placement sweep
   * derives its slack from this — pinned geometry never translates. */
  freeRequiredEnvelope?: GenerationTestFitEnvelope;
  rectangles: readonly GenerationTestFitRectangle[];
  totalComponentAreaAcres: number;
  componentAreaMarginAcres: number;
  componentAreaUtilizationPct: number;
  widthOverflowM: number;
  heightOverflowM: number;
  orientationEvaluations: readonly GenerationTestFitOrientationEvaluation[];
  /** Present only when packingScheme was requested. */
  packingScheme?: GenerationTestFitPackingScheme;
  /** All evaluated scheme x orientation candidates; present only when
   * packingScheme was requested. */
  schemeEvaluations?: readonly GenerationTestFitSchemeEvaluation[];
  reservationSummary: {
    permanentEquipmentAcres: number;
    routeReservationAcres: number;
    constructionLaydownAcres: number;
    totalReservedAcres: number;
  };
  caveats: readonly string[];
}

interface TestFitItem {
  id: string;
  label: string;
  kind: GenerationTestFitComponentKind;
  blockNumber?: number;
  areaAcres: number;
  logicalWidthM: number;
  logicalHeightM: number;
  dimensionBasis: GenerationTestFitDimensionBasis;
  lifecycle: GenerationTestFitLifecycle;
}

interface LogicalPlacement extends TestFitItem {
  logicalXM: number;
  logicalYM: number;
}

interface Candidate {
  orientation: GenerationTestFitResolvedOrientation;
  scheme: GenerationTestFitPackingScheme;
  rectangles: GenerationTestFitRectangle[];
  requiredWidthM: number;
  requiredHeightM: number;
  /** Packed-group extents WITHOUT setbacks, in the final (transposed) frame. */
  packedWidthM: number;
  packedHeightM: number;
  widthOverflowM: number;
  heightOverflowM: number;
  fits: boolean;
}

const MIN_DIMENSION_M = 0.01;
const MAX_DIMENSION_M = 1_000_000;
const MAX_CLEAR_DISTANCE_M = 100_000;
const MAX_FOOTPRINT_ACRES = 100_000_000;
const MAX_GENERATION_BLOCKS = 10_000;
const MIN_ASPECT_RATIO = 0.01;
const MAX_ASPECT_RATIO = 100;
const MAX_ROTATION_DEG = 360;
const FIT_TOLERANCE_M = 1e-8;

const DEFAULT_ASPECT_RATIOS: Required<GenerationTestFitAspectRatios> = {
  generationBlock: 1,
  cooling: 1,
  switchyard: 1,
  ccsCapture: 1,
  ccsCompression: 1,
};

const TEST_FIT_CAVEATS = [
  'Screening geometry only: axis-aligned shelf packing does not represent vendor equipment clearances, roads, grading, drainage, fire access or constructability.',
  'Before the model applies edgeSetbackM, the supplied envelope must already exclude easements, wetlands and other irregular no-build areas.',
  'Planning-acreage components use neutral geometric surrogates; replace every remaining surrogate with project dimensions before design use.',
] as const;

function assertFiniteInRange(
  value: number,
  label: string,
  min: number,
  max: number,
): void {
  if (!Number.isFinite(value) || value < min || value > max) {
    throw new RangeError(
      `${label} must be finite and between ${min} and ${max}; received ${value}`,
    );
  }
}

function validateInput(input: GenerationTestFitInput): void {
  assertFiniteInRange(
    input.usableEnvelope.widthM,
    'Usable envelope width',
    MIN_DIMENSION_M,
    MAX_DIMENSION_M,
  );
  assertFiniteInRange(
    input.usableEnvelope.heightM,
    'Usable envelope height',
    MIN_DIMENSION_M,
    MAX_DIMENSION_M,
  );
  assertFiniteInRange(
    input.generationBlockCount,
    'Generation block count',
    1,
    MAX_GENERATION_BLOCKS,
  );
  if (!Number.isInteger(input.generationBlockCount)) {
    throw new RangeError(
      `Generation block count must be an integer; received ${input.generationBlockCount}`,
    );
  }
  assertFiniteInRange(input.edgeSetbackM, 'Edge setback', 0, MAX_CLEAR_DISTANCE_M);
  assertFiniteInRange(input.componentGapM, 'Inter-component gap', 0, MAX_CLEAR_DISTANCE_M);
  const packingWidthM = input.usableEnvelope.widthM - 2 * input.edgeSetbackM;
  const packingHeightM = input.usableEnvelope.heightM - 2 * input.edgeSetbackM;
  if (packingWidthM <= 0 || packingHeightM <= 0) {
    throw new RangeError(
      `Edge setback leaves no usable packing envelope; received ${input.edgeSetbackM} m inside ${input.usableEnvelope.widthM} m × ${input.usableEnvelope.heightM} m`,
    );
  }

  const footprintEntries: ReadonlyArray<readonly [string, number]> = [
    ['Plant footprint', input.footprint.plantAcres],
    ['Cooling footprint', input.footprint.coolingAcres],
    ['Switchyard footprint', input.footprint.switchyardAcres],
    ['CCS capture footprint', input.footprint.ccsAcres],
    ['CCS compression footprint', input.footprint.compressionAcres],
  ];
  for (const [label, acres] of footprintEntries) {
    assertFiniteInRange(acres, label, 0, MAX_FOOTPRINT_ACRES);
  }
  const hasSharedOrInstalledArea = input.footprint.coolingAcres > 0
    || input.footprint.switchyardAcres > 0
    || input.footprint.ccsAcres > 0
    || input.footprint.compressionAcres > 0
    || Boolean(input.installedReservations?.generationBlockEnvelope)
    || Boolean(input.installedReservations?.coolingEnvelope)
    || Boolean(input.installedReservations?.switchyardEnvelope);
  if (input.footprint.plantAcres <= 0 && !hasSharedOrInstalledArea) {
    throw new RangeError(
      `Plant footprint must be greater than zero; received ${input.footprint.plantAcres}`,
    );
  }

  const aspectRatioEntries: ReadonlyArray<readonly [string, number | undefined]> = [
    ['Generation-block aspect ratio', input.aspectRatios?.generationBlock],
    ['Cooling aspect ratio', input.aspectRatios?.cooling],
    ['Switchyard aspect ratio', input.aspectRatios?.switchyard],
    ['CCS capture aspect ratio', input.aspectRatios?.ccsCapture],
    ['CCS compression aspect ratio', input.aspectRatios?.ccsCompression],
  ];
  for (const [label, ratio] of aspectRatioEntries) {
    if (ratio != null) {
      assertFiniteInRange(ratio, label, MIN_ASPECT_RATIO, MAX_ASPECT_RATIO);
    }
  }

  const orientation = input.orientation ?? 'auto';
  if (
    orientation !== 'auto'
    && orientation !== 'blocks-along-width'
    && orientation !== 'blocks-along-height'
  ) {
    throw new RangeError(`Unsupported test-fit orientation: ${String(orientation)}`);
  }

  if (input.rowRoadWidthM != null) {
    assertFiniteInRange(input.rowRoadWidthM, 'Row road width', 0, MAX_CLEAR_DISTANCE_M);
  }
  const packingScheme = input.packingScheme ?? 'compact';
  if (packingScheme !== 'compact' && packingScheme !== 'road-rows' && packingScheme !== 'auto') {
    throw new RangeError(`Unsupported packing scheme: ${String(packingScheme)}`);
  }

  if (input.schemaVersion !== undefined && input.schemaVersion !== 2) {
    throw new RangeError(`Unsupported test-fit schema version: ${String(input.schemaVersion)}`);
  }
  if ((input.manualPlacements?.length ?? 0) > 0 && input.schemaVersion !== 2) {
    throw new RangeError('Manual placements require layout schemaVersion 2.');
  }
  for (const placement of input.manualPlacements ?? []) {
    if (typeof placement.componentId !== 'string' || placement.componentId.length === 0) {
      throw new RangeError('Manual placement componentId must be a non-empty string.');
    }
    assertFiniteInRange(
      placement.xM,
      `Manual placement ${placement.componentId} xM`,
      -MAX_DIMENSION_M,
      MAX_DIMENSION_M,
    );
    assertFiniteInRange(
      placement.yM,
      `Manual placement ${placement.componentId} yM`,
      -MAX_DIMENSION_M,
      MAX_DIMENSION_M,
    );
    assertFiniteInRange(
      placement.rotationDeg,
      `Manual placement ${placement.componentId} rotation`,
      -MAX_ROTATION_DEG,
      MAX_ROTATION_DEG,
    );
  }

  const installed = input.installedReservations;
  if (installed) {
    if (!Number.isInteger(installed.blockQuantity)
      || installed.blockQuantity !== input.generationBlockCount) {
      throw new RangeError(
        `Installed reservation block quantity must equal generation block count; received ${installed.blockQuantity} and ${input.generationBlockCount}.`,
      );
    }
    const envelopes: ReadonlyArray<readonly [string, GenerationProjectReservationEnvelope | undefined]> = [
      ['Generation block envelope', installed.generationBlockEnvelope],
      ['Cooling envelope', installed.coolingEnvelope],
      ['Switchyard envelope', installed.switchyardEnvelope],
      ['CCS capture envelope', installed.ccsCaptureEnvelope],
      ['CCS compression envelope', installed.ccsCompressionEnvelope],
      ['Natural-gas route envelope', installed.naturalGasRouteEnvelope],
      ['CO2 export route envelope', installed.co2ExportRouteEnvelope],
      ['Construction laydown envelope', installed.constructionLaydownEnvelope],
    ];
    envelopes.forEach(([label, envelope]) => {
      if (!envelope) return;
      assertFiniteInRange(envelope.lengthM, `${label} length`, MIN_DIMENSION_M, MAX_DIMENSION_M);
      assertFiniteInRange(envelope.widthM, `${label} width`, MIN_DIMENSION_M, MAX_DIMENSION_M);
    });
  }
}

function dimensionsForArea(areaAcres: number, aspectRatio: number): {
  logicalWidthM: number;
  logicalHeightM: number;
} {
  const areaM2 = areaAcres * SQUARE_METRES_PER_ACRE;
  return {
    logicalWidthM: Math.sqrt(areaM2 * aspectRatio),
    logicalHeightM: Math.sqrt(areaM2 / aspectRatio),
  };
}

function item(
  id: string,
  label: string,
  kind: GenerationTestFitComponentKind,
  areaAcres: number,
  aspectRatio: number,
  blockNumber?: number,
  envelope?: GenerationProjectReservationEnvelope,
  dimensionBasis: GenerationTestFitDimensionBasis = 'planning-acreage',
  lifecycle: GenerationTestFitLifecycle = 'permanent',
): TestFitItem {
  const dimensions = envelope
    ? { logicalWidthM: envelope.lengthM, logicalHeightM: envelope.widthM }
    : dimensionsForArea(areaAcres, aspectRatio);
  const resolvedAreaAcres = envelope
    ? envelope.lengthM * envelope.widthM / SQUARE_METRES_PER_ACRE
    : areaAcres;
  return {
    id,
    label,
    kind,
    ...(blockNumber == null ? {} : { blockNumber }),
    areaAcres: resolvedAreaAcres,
    ...dimensions,
    dimensionBasis,
    lifecycle,
  };
}

function buildItems(input: GenerationTestFitInput): TestFitItem[] {
  const ratios: Required<GenerationTestFitAspectRatios> = {
    generationBlock: input.aspectRatios?.generationBlock
      ?? DEFAULT_ASPECT_RATIOS.generationBlock,
    cooling: input.aspectRatios?.cooling ?? DEFAULT_ASPECT_RATIOS.cooling,
    switchyard: input.aspectRatios?.switchyard ?? DEFAULT_ASPECT_RATIOS.switchyard,
    ccsCapture: input.aspectRatios?.ccsCapture ?? DEFAULT_ASPECT_RATIOS.ccsCapture,
    ccsCompression: input.aspectRatios?.ccsCompression
      ?? DEFAULT_ASPECT_RATIOS.ccsCompression,
  };
  const plantAcresPerBlock = input.footprint.plantAcres / input.generationBlockCount;
  const installed = input.installedReservations;
  const ccsActive = input.footprint.ccsAcres > 0
    || input.footprint.compressionAcres > 0;
  const items: TestFitItem[] = (plantAcresPerBlock > 0 || installed?.generationBlockEnvelope)
    ? Array.from({ length: input.generationBlockCount }, (_, index) => item(
        `generation-block-${index + 1}`,
        `Generation block ${index + 1}`,
        'generation-block',
        plantAcresPerBlock,
        ratios.generationBlock,
        index + 1,
        installed?.generationBlockEnvelope,
        installed ? 'project-installed-dimensions' : 'planning-acreage',
      ))
    : [];

  const sharedItems: ReadonlyArray<{
    id: string;
    label: string;
    kind: GenerationTestFitComponentKind;
    areaAcres: number;
    aspectRatio: number;
    envelope?: GenerationProjectReservationEnvelope;
  }> = [
    {
      id: 'cooling',
      label: 'Cooling system',
      kind: 'cooling',
      areaAcres: input.footprint.coolingAcres,
      aspectRatio: ratios.cooling,
      envelope: installed?.coolingEnvelope,
    },
    {
      id: 'switchyard',
      label: 'Switchyard',
      kind: 'switchyard',
      areaAcres: input.footprint.switchyardAcres,
      aspectRatio: ratios.switchyard,
      envelope: installed?.switchyardEnvelope,
    },
    {
      id: 'ccs-capture',
      label: 'CCS capture system',
      kind: 'ccs-capture',
      areaAcres: input.footprint.ccsAcres,
      aspectRatio: ratios.ccsCapture,
      envelope: ccsActive ? installed?.ccsCaptureEnvelope : undefined,
    },
    {
      id: 'ccs-compression',
      label: 'CCS compression',
      kind: 'ccs-compression',
      areaAcres: input.footprint.compressionAcres,
      aspectRatio: ratios.ccsCompression,
      envelope: ccsActive ? installed?.ccsCompressionEnvelope : undefined,
    },
  ];

  for (const shared of sharedItems) {
    if (shared.areaAcres > 0 || shared.envelope) {
      items.push(item(
        shared.id,
        shared.label,
        shared.kind,
        shared.areaAcres,
        shared.aspectRatio,
        undefined,
        shared.envelope,
        shared.envelope ? 'project-installed-dimensions' : 'planning-acreage',
      ));
    }
  }
  const reservationItems: ReadonlyArray<{
    id: string;
    label: string;
    kind: GenerationTestFitComponentKind;
    envelope: GenerationProjectReservationEnvelope | undefined;
    dimensionBasis: GenerationTestFitDimensionBasis;
    lifecycle: GenerationTestFitLifecycle;
  }> = [
    {
      id: 'natural-gas-route',
      label: 'Natural-gas route reservation',
      kind: 'natural-gas-route',
      envelope: installed?.naturalGasRouteEnvelope,
      dimensionBasis: 'project-route-reservation',
      lifecycle: 'route-reservation',
    },
    {
      id: 'co2-export-route',
      label: 'CO2 export route reservation',
      kind: 'co2-export-route',
      envelope: ccsActive ? installed?.co2ExportRouteEnvelope : undefined,
      dimensionBasis: 'project-route-reservation',
      lifecycle: 'route-reservation',
    },
    {
      id: 'construction-laydown',
      label: 'Construction laydown reservation',
      kind: 'construction-laydown',
      envelope: installed?.constructionLaydownEnvelope,
      dimensionBasis: 'project-construction-reservation',
      lifecycle: 'construction',
    },
  ];
  reservationItems.forEach(reservation => {
    if (!reservation.envelope) return;
    items.push(item(
      reservation.id,
      reservation.label,
      reservation.kind,
      0,
      1,
      undefined,
      reservation.envelope,
      reservation.dimensionBasis,
      reservation.lifecycle,
    ));
  });
  return items;
}

interface PackedShelfRow {
  /** Logical Y of the row's bottom edge. */
  topYM: number;
  heightM: number;
  hasBlock: boolean;
}

function packShelves(
  items: readonly TestFitItem[],
  logicalEnvelopeWidthM: number,
  componentGapM: number,
  blockRowGapM: number = componentGapM,
): { placements: LogicalPlacement[]; widthM: number; heightM: number; rows: PackedShelfRow[] } {
  const placements: LogicalPlacement[] = [];
  const rows: PackedShelfRow[] = [];
  let logicalXM = 0;
  let logicalYM = 0;
  let rowHeightM = 0;
  let requiredWidthM = 0;
  let rowHasBlock = false;

  const closeRow = (): void => {
    rows.push({ topYM: logicalYM, heightM: rowHeightM, hasBlock: rowHasBlock });
  };

  for (const current of items) {
    const wouldExceedRow = logicalXM > 0
      && logicalXM + current.logicalWidthM > logicalEnvelopeWidthM + FIT_TOLERANCE_M;
    if (wouldExceedRow) {
      closeRow();
      logicalXM = 0;
      // A row containing generation blocks reserves the access-road corridor
      // below it; other rows keep the standard component gap.
      logicalYM += rowHeightM + (rowHasBlock ? blockRowGapM : componentGapM);
      rowHeightM = 0;
      rowHasBlock = false;
    }

    placements.push({ ...current, logicalXM, logicalYM });
    requiredWidthM = Math.max(requiredWidthM, logicalXM + current.logicalWidthM);
    rowHeightM = Math.max(rowHeightM, current.logicalHeightM);
    logicalXM += current.logicalWidthM + componentGapM;
    rowHasBlock = rowHasBlock || current.kind === 'generation-block';
  }
  if (placements.length > 0) closeRow();

  return {
    placements,
    widthM: requiredWidthM,
    heightM: logicalYM + rowHeightM,
    rows,
  };
}

function closedPolygon(
  xM: number,
  yM: number,
  widthM: number,
  heightM: number,
): readonly GenerationTestFitLocalPoint[] {
  return [
    { xM, yM },
    { xM: xM + widthM, yM },
    { xM: xM + widthM, yM: yM + heightM },
    { xM, yM: yM + heightM },
    { xM, yM },
  ];
}

/**
 * Closed 5-point ring for a rectangle rotated CCW about its CENTRE, in the
 * same vertex order as {@link closedPolygon} (lower-left first, CCW). Exact
 * trig — at 0° the ring is bit-identical to the axis-aligned form.
 */
export function rotatedClosedPolygon(
  centreXM: number,
  centreYM: number,
  widthM: number,
  heightM: number,
  rotationDeg: number,
): readonly GenerationTestFitLocalPoint[] {
  const theta = rotationDeg * Math.PI / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const halfW = widthM / 2;
  const halfH = heightM / 2;
  const corners: ReadonlyArray<readonly [number, number]> = [
    [-halfW, -halfH],
    [halfW, -halfH],
    [halfW, halfH],
    [-halfW, halfH],
  ];
  const ring = corners.map(([dx, dy]) => ({
    xM: centreXM + dx * cos - dy * sin,
    yM: centreYM + dx * sin + dy * cos,
  }));
  return [...ring, { ...ring[0]! }];
}

function makeCandidate(
  input: GenerationTestFitInput,
  items: readonly TestFitItem[],
  orientation: GenerationTestFitResolvedOrientation,
  scheme: GenerationTestFitPackingScheme = 'compact',
): Candidate {
  const transposed = orientation === 'blocks-along-height';
  const studio = input.schemaVersion === 2;
  const roadWidthM = input.rowRoadWidthM ?? 0;
  const packingWidthM = input.usableEnvelope.widthM - 2 * input.edgeSetbackM;
  const packingHeightM = input.usableEnvelope.heightM - 2 * input.edgeSetbackM;
  const logicalEnvelopeWidthM = transposed
    ? packingHeightM
    : packingWidthM;
  const blockRowGapM = scheme === 'road-rows'
    ? (studio
        // schemaVersion 2: the corridor is ADDITIVE — the road reserves its
        // own width on top of the component gap, so a road narrower than the
        // gap is never a silent no-op.
        ? input.componentGapM + roadWidthM
        // Legacy (frozen replay): max() swallows roads <= componentGapM.
        : Math.max(input.componentGapM, roadWidthM))
    : input.componentGapM;
  const packed = packShelves(items, logicalEnvelopeWidthM, input.componentGapM, blockRowGapM);
  const packedWidthM = transposed ? packed.heightM : packed.widthM;
  const packedHeightM = transposed ? packed.widthM : packed.heightM;
  const requiredWidthM = packedWidthM + 2 * input.edgeSetbackM;
  const requiredHeightM = packedHeightM + 2 * input.edgeSetbackM;
  const widthOverflowM = Math.max(0, requiredWidthM - input.usableEnvelope.widthM);
  const heightOverflowM = Math.max(0, requiredHeightM - input.usableEnvelope.heightM);
  const fits = widthOverflowM <= FIT_TOLERANCE_M && heightOverflowM <= FIT_TOLERANCE_M;

  // Centre the packed geometry on the usable parcel envelope. This keeps the
  // local output independent of a southwest/northeast geospatial convention
  // and lets a caller apply one rotation/translation at the parcel centroid.
  const packedOriginXM = -packedWidthM / 2;
  const packedOriginYM = -packedHeightM / 2;

  const rectangles = packed.placements.map((placement): GenerationTestFitRectangle => {
    const rectangleOriginXM = packedOriginXM
      + (transposed ? placement.logicalYM : placement.logicalXM);
    const rectangleOriginYM = packedOriginYM
      + (transposed ? placement.logicalXM : placement.logicalYM);
    const widthM = transposed ? placement.logicalHeightM : placement.logicalWidthM;
    const heightM = transposed ? placement.logicalWidthM : placement.logicalHeightM;
    const xM = rectangleOriginXM + widthM / 2;
    const yM = rectangleOriginYM + heightM / 2;
    const halfPackingWidthM = packingWidthM / 2;
    const halfPackingHeightM = packingHeightM / 2;
    const fitsWithinEnvelope = rectangleOriginXM >= -halfPackingWidthM - FIT_TOLERANCE_M
      && rectangleOriginYM >= -halfPackingHeightM - FIT_TOLERANCE_M
      && rectangleOriginXM + widthM <= halfPackingWidthM + FIT_TOLERANCE_M
      && rectangleOriginYM + heightM <= halfPackingHeightM + FIT_TOLERANCE_M;
    return {
      id: placement.id,
      label: placement.label,
      kind: placement.kind,
      ...(placement.blockNumber == null ? {} : { blockNumber: placement.blockNumber }),
      areaAcres: placement.areaAcres,
      xM,
      yM,
      widthM,
      heightM,
      polygonM: closedPolygon(rectangleOriginXM, rectangleOriginYM, widthM, heightM),
      fitsWithinEnvelope,
      dimensionBasis: placement.dimensionBasis,
      lifecycle: placement.lifecycle,
    };
  });

  // schemaVersion 2 road-rows: emit the reserved corridors as visible
  // access-road rectangles. Each corridor sits centred in the additive row
  // gap (componentGapM of clearance splits evenly around the road band), so
  // the emitted geometry occupies space packShelves already reserved and the
  // candidate envelope is unchanged.
  if (studio && scheme === 'road-rows' && roadWidthM > 0) {
    let roadNumber = 0;
    for (let rowIndex = 0; rowIndex < packed.rows.length - 1; rowIndex += 1) {
      const row = packed.rows[rowIndex]!;
      if (!row.hasBlock) continue;
      roadNumber += 1;
      const logicalYM = row.topYM + row.heightM + input.componentGapM / 2;
      const originXM = packedOriginXM + (transposed ? logicalYM : 0);
      const originYM = packedOriginYM + (transposed ? 0 : logicalYM);
      const widthM = transposed ? roadWidthM : packed.widthM;
      const heightM = transposed ? packed.widthM : roadWidthM;
      const halfPackingWidthM = packingWidthM / 2;
      const halfPackingHeightM = packingHeightM / 2;
      rectangles.push({
        id: `access-road-${roadNumber}`,
        label: `Access road ${roadNumber}`,
        kind: 'access-road',
        areaAcres: widthM * heightM / SQUARE_METRES_PER_ACRE,
        xM: originXM + widthM / 2,
        yM: originYM + heightM / 2,
        widthM,
        heightM,
        polygonM: closedPolygon(originXM, originYM, widthM, heightM),
        fitsWithinEnvelope: originXM >= -halfPackingWidthM - FIT_TOLERANCE_M
          && originYM >= -halfPackingHeightM - FIT_TOLERANCE_M
          && originXM + widthM <= halfPackingWidthM + FIT_TOLERANCE_M
          && originYM + heightM <= halfPackingHeightM + FIT_TOLERANCE_M,
        dimensionBasis: 'planning-acreage',
        lifecycle: 'permanent',
      });
    }
  }

  return {
    orientation,
    scheme,
    rectangles,
    requiredWidthM,
    requiredHeightM,
    packedWidthM,
    packedHeightM,
    widthOverflowM,
    heightOverflowM,
    fits,
  };
}

function candidateScore(candidate: Candidate, envelope: GenerationTestFitEnvelope): readonly number[] {
  const overflowFraction = candidate.widthOverflowM / envelope.widthM
    + candidate.heightOverflowM / envelope.heightM;
  const largestSpanFraction = Math.max(
    candidate.requiredWidthM / envelope.widthM,
    candidate.requiredHeightM / envelope.heightM,
  );
  const boundingArea = candidate.requiredWidthM * candidate.requiredHeightM;
  // Among FITTING candidates, road-rows (with its access corridors) beats
  // compact; overflowing candidates compete purely on overflow so a fitting
  // compact plan always beats a spilling road-rows plan. With a single scheme
  // in play the rank is constant and the legacy ordering is unchanged.
  const schemeRank = candidate.fits ? (candidate.scheme === 'road-rows' ? 0 : 1) : 0;
  return [candidate.fits ? 0 : 1, schemeRank, overflowFraction, largestSpanFraction, boundingArea];
}

function compareCandidates(
  left: Candidate,
  right: Candidate,
  envelope: GenerationTestFitEnvelope,
): number {
  const leftScore = candidateScore(left, envelope);
  const rightScore = candidateScore(right, envelope);
  for (let index = 0; index < leftScore.length; index += 1) {
    const difference = leftScore[index]! - rightScore[index]!;
    if (Math.abs(difference) > Number.EPSILON) return difference;
  }
  if (left.orientation !== right.orientation) {
    return left.orientation === 'blocks-along-width' ? -1 : 1;
  }
  // Identical geometry across schemes (e.g. a single block row): prefer the
  // compact label so the legacy v2 model version is retained.
  return left.scheme === 'compact' ? -1 : 1;
}

function evaluation(candidate: Candidate): GenerationTestFitOrientationEvaluation {
  return {
    orientation: candidate.orientation,
    fits: candidate.fits,
    requiredWidthM: candidate.requiredWidthM,
    requiredHeightM: candidate.requiredHeightM,
    widthOverflowM: candidate.widthOverflowM,
    heightOverflowM: candidate.heightOverflowM,
    boundingAreaAcres: candidate.requiredWidthM
      * candidate.requiredHeightM
      / SQUARE_METRES_PER_ACRE,
  };
}

function schemeEvaluation(candidate: Candidate): GenerationTestFitSchemeEvaluation {
  return { ...evaluation(candidate), scheme: candidate.scheme };
}

/**
 * Build a deterministic screening test-fit inside a rectangular usable parcel.
 *
 * Rectangles preserve the exact supplied component acreages. Packing proceeds
 * in a stable order (generation blocks, cooling, switchyard, CCS capture,
 * compression) and wraps to a new shelf when the selected envelope axis is
 * full. `auto` evaluates both orthogonal orientations and chooses a fit before
 * an overflow; ties prefer the lower normalized span, then width orientation.
 */
export function buildGenerationTestFit(
  input: GenerationTestFitInput,
): GenerationTestFitResult {
  validateInput(input);
  const items = buildItems(input);
  const studio = input.schemaVersion === 2;

  // Pin+pack partition (schemaVersion 2): pinned items leave the shelf pack
  // and place exactly at their layout-frame coordinates, identical across
  // every orientation/scheme candidate. Duplicate pins resolve last-wins;
  // pins for components not in this plan are dropped with a caveat.
  const pinByComponentId = new Map<string, GenerationLayoutManualPlacement>();
  if (studio) {
    for (const placement of input.manualPlacements ?? []) {
      pinByComponentId.set(placement.componentId, placement);
    }
  }
  const pinnedItems = items.filter(current => pinByComponentId.has(current.id));
  const freeItems = pinnedItems.length > 0
    ? items.filter(current => !pinByComponentId.has(current.id))
    : items;
  const stalePinIds = [...pinByComponentId.keys()]
    .filter(componentId => !items.some(current => current.id === componentId))
    .sort();
  const packingWidthM = input.usableEnvelope.widthM - 2 * input.edgeSetbackM;
  const packingHeightM = input.usableEnvelope.heightM - 2 * input.edgeSetbackM;
  const halfPackingWidthM = packingWidthM / 2;
  const halfPackingHeightM = packingHeightM / 2;
  const pinnedRectangles = pinnedItems.map((current): GenerationTestFitRectangle => {
    const pin = pinByComponentId.get(current.id)!;
    const polygonM = rotatedClosedPolygon(
      pin.xM,
      pin.yM,
      current.logicalWidthM,
      current.logicalHeightM,
      pin.rotationDeg,
    );
    const fitsWithinEnvelope = polygonM.every(point => (
      point.xM >= -halfPackingWidthM - FIT_TOLERANCE_M
      && point.xM <= halfPackingWidthM + FIT_TOLERANCE_M
      && point.yM >= -halfPackingHeightM - FIT_TOLERANCE_M
      && point.yM <= halfPackingHeightM + FIT_TOLERANCE_M
    ));
    return {
      id: current.id,
      label: current.label,
      kind: current.kind,
      ...(current.blockNumber == null ? {} : { blockNumber: current.blockNumber }),
      areaAcres: current.areaAcres,
      xM: pin.xM,
      yM: pin.yM,
      widthM: current.logicalWidthM,
      heightM: current.logicalHeightM,
      polygonM,
      fitsWithinEnvelope,
      dimensionBasis: current.dimensionBasis,
      lifecycle: current.lifecycle,
      rotationDeg: pin.rotationDeg,
      placement: 'pinned',
    };
  });
  const pinsActive = pinnedRectangles.length > 0;

  const requestedScheme = input.packingScheme ?? 'compact';
  // Legacy `auto` only considers road-rows when the road exceeds the gap
  // (because max() makes anything narrower a no-op). With the additive
  // corridor any road width > 0 is meaningful.
  const roadRowsMeaningful = studio
    ? (input.rowRoadWidthM ?? 0) > 0
    : (input.rowRoadWidthM ?? 0) > input.componentGapM;
  const schemes: readonly GenerationTestFitPackingScheme[] = requestedScheme === 'auto'
    ? (roadRowsMeaningful ? ['compact', 'road-rows'] : ['compact'])
    : requestedScheme === 'road-rows'
      ? ['road-rows']
      : ['compact'];
  const candidates = schemes.flatMap(scheme => [
    makeCandidate(input, freeItems, 'blocks-along-width', scheme),
    makeCandidate(input, freeItems, 'blocks-along-height', scheme),
  ]);
  const requestedOrientation = input.orientation ?? 'auto';
  const eligible = requestedOrientation === 'auto'
    ? candidates
    : candidates.filter(candidate => candidate.orientation === requestedOrientation);
  const selected = [...eligible].sort((left, right) => compareCandidates(
    left,
    right,
    input.usableEnvelope,
  ))[0]!;

  const usableAreaAcres = input.usableEnvelope.widthM
    * input.usableEnvelope.heightM
    / SQUARE_METRES_PER_ACRE;
  const packingAreaAcres = packingWidthM * packingHeightM / SQUARE_METRES_PER_ACRE;

  // With pins, the reported required envelope covers the packed group AND
  // every pinned rectangle (setbacks on all sides); the free-group envelope
  // is preserved separately for the placement sweep. Legacy path untouched.
  const selectedRoadRectangles = selected.rectangles
    .filter(rectangle => rectangle.kind === 'access-road');
  const selectedRoadAcres = selectedRoadRectangles
    .reduce((sum, rectangle) => sum + rectangle.areaAcres, 0);
  let requiredWidthM = selected.requiredWidthM;
  let requiredHeightM = selected.requiredHeightM;
  let widthOverflowM = selected.widthOverflowM;
  let heightOverflowM = selected.heightOverflowM;
  if (pinsActive) {
    const xs: number[] = [];
    const ys: number[] = [];
    if (selected.rectangles.length > 0) {
      xs.push(-selected.packedWidthM / 2, selected.packedWidthM / 2);
      ys.push(-selected.packedHeightM / 2, selected.packedHeightM / 2);
    }
    for (const rectangle of pinnedRectangles) {
      for (const point of rectangle.polygonM) {
        xs.push(point.xM);
        ys.push(point.yM);
      }
    }
    requiredWidthM = Math.max(...xs) - Math.min(...xs) + 2 * input.edgeSetbackM;
    requiredHeightM = Math.max(...ys) - Math.min(...ys) + 2 * input.edgeSetbackM;
    widthOverflowM = Math.max(
      selected.widthOverflowM,
      requiredWidthM - input.usableEnvelope.widthM,
      0,
    );
    heightOverflowM = Math.max(
      selected.heightOverflowM,
      requiredHeightM - input.usableEnvelope.heightM,
      0,
    );
  }
  const requiredAreaAcres = requiredWidthM
    * requiredHeightM
    / SQUARE_METRES_PER_ACRE;
  const itemsAreaAcres = items.reduce((sum, current) => sum + current.areaAcres, 0);
  // Emitted access-road corridors consume real ground: they count toward the
  // component total (and therefore containment denominators) but are not
  // equipment/route/laydown reservations. Legacy inputs never emit roads, so
  // the legacy value is bit-identical (x + 0 === x).
  const totalComponentAreaAcres = itemsAreaAcres + selectedRoadAcres;
  const reservationSummary = {
    permanentEquipmentAcres: items
      .filter(current => current.lifecycle === 'permanent')
      .reduce((sum, current) => sum + current.areaAcres, 0),
    routeReservationAcres: items
      .filter(current => current.lifecycle === 'route-reservation')
      .reduce((sum, current) => sum + current.areaAcres, 0),
    constructionLaydownAcres: items
      .filter(current => current.lifecycle === 'construction')
      .reduce((sum, current) => sum + current.areaAcres, 0),
    totalReservedAcres: itemsAreaAcres,
  };
  const pinnedAllFit = pinnedRectangles.every(rectangle => rectangle.fitsWithinEnvelope);

  return {
    modelVersion: studio
      ? (pinsActive || selectedRoadRectangles.length > 0
          ? 'generation-test-fit-v4'
          : 'generation-test-fit-v2')
      : selected.scheme === 'road-rows'
        ? 'generation-test-fit-v3'
        : 'generation-test-fit-v2',
    status: selected.fits && pinnedAllFit ? 'fit' : 'overflow',
    orientation: selected.orientation,
    usableEnvelope: {
      ...input.usableEnvelope,
      areaAcres: usableAreaAcres,
    },
    packingEnvelope: {
      widthM: packingWidthM,
      heightM: packingHeightM,
      areaAcres: packingAreaAcres,
    },
    requiredEnvelope: {
      widthM: requiredWidthM,
      heightM: requiredHeightM,
      areaAcres: requiredAreaAcres,
    },
    ...(pinsActive
      ? {
          freeRequiredEnvelope: {
            widthM: selected.requiredWidthM,
            heightM: selected.requiredHeightM,
          },
        }
      : {}),
    rectangles: pinsActive
      ? [...selected.rectangles, ...pinnedRectangles]
      : selected.rectangles,
    totalComponentAreaAcres,
    componentAreaMarginAcres: packingAreaAcres - totalComponentAreaAcres,
    componentAreaUtilizationPct: totalComponentAreaAcres / packingAreaAcres * 100,
    widthOverflowM,
    heightOverflowM,
    orientationEvaluations: candidates
      .filter(candidate => candidate.scheme === selected.scheme)
      .map(evaluation),
    ...(input.packingScheme != null
      ? {
          packingScheme: selected.scheme,
          schemeEvaluations: candidates.map(schemeEvaluation),
        }
      : {}),
    reservationSummary,
    caveats: [
      ...TEST_FIT_CAVEATS,
      ...(selected.scheme === 'road-rows'
        ? [studio
            ? 'Access-road corridors between generation-block rows add the entered road width to the component spacing and are emitted as access-road components; they are screening reservations, not designed roadways.'
            : 'Access-road corridors between generation-block rows are screening reservations (the larger of component spacing and the entered road width); they do not represent designed roadways.']
        : []),
      ...(pinsActive
        ? ['Pinned components are placed exactly as entered; automatic packing does not move them or resolve overlaps with pinned geometry — review reported overlaps before relying on this plan.']
        : []),
      ...(stalePinIds.length > 0
        ? [`Manual placements ignored for components not in this plan: ${stalePinIds.join(', ')}.`]
        : []),
      ...(input.installedReservations
        ? [
            'Project-installed dimensions override acreage surrogates only for the explicitly supplied envelopes; route rectangles reserve width and length but do not prove final pipeline connectivity.',
            ...(!input.installedReservations.generationBlockEnvelope.maintenanceClearanceIncluded
              ? ['The project generation-block envelope does not include maintenance clearance.']
              : []),
            ...(!input.installedReservations.generationBlockEnvelope.fireAccessIncluded
              ? ['The project generation-block envelope does not include fire-access allowance.']
              : []),
          ]
        : [
            'Total plant acreage is divided equally among generation blocks; use a block-specific installed envelope before design use.',
          ]),
    ],
  };
}
